import { useMediaQuery } from "usehooks-ts";

import { mobile } from "./mobile";

// One pixel below Clients.App's Screen.md / @bui-token-screen-md (768px), i.e. its
// ScreenMax.sm. Inlined rather than imported because Clients.App's styles module isn't
// reachable from this package, matching how BTSplitView declares its own collapse
// breakpoint. Keep the two in step: this is the width at which a split view has already
// pushed its list out and an overlay owns the whole screen.
const MOBILE_BREAKPOINT_MAX_PX = 767;

/**
 * True in the mobile WebView at a phone-width viewport — the case where a full-screen
 * overlay should not render its own close "X", because native back is the single
 * dismissal affordance.
 *
 * Both halves matter. `mobile.isWebview` alone is true in the WebView at tablet width,
 * where an overlay is still a floating card with room for its own X and hiding it would
 * strand the user. A width check alone is true in a narrow desktop browser, where there
 * is no native back button at all.
 *
 * Unlike {@link useIsNativeBackReplacingClose}, this does **not** mean anything claimed
 * the native back button. It covers the route-driven overlays that must *not* claim it
 * (back already pops their route) but should still drop a redundant X. The two are
 * independent reasons to hide a close control; a surface may consult either or both.
 *
 * Keys on `mobile.isWebview` rather than `useWebviewContext()`: the context is
 * route-scoped, so it is true for a `/webview/...` URL opened in a desktop browser —
 * where there is no native back button to fall back on — and false for a child route
 * rendered inside a real WebView.
 *
 * @example
 * ```tsx
 * const isMobileViewportWebview = useIsMobileViewportWebview();
 * {!isMobileViewportWebview && <BTButton icon={<IconX />} aria-label="Close" onClick={onClose} />}
 * ```
 */
export function useIsMobileViewportWebview(): boolean {
  const isMobileWidth = useMediaQuery(
    `(max-width: ${MOBILE_BREAKPOINT_MAX_PX}px)`,
  );

  return isMobileWidth && mobile.isWebview;
}
